import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { loginSchema, mfaVerifySchema, type LoginInput, type MfaVerifyInput, type SessionResponse } from '@orion/contracts';
import { Button, Input } from '@orion/ui';
import { useAuth } from '../../lib/auth.js';
import { useToast } from '../../lib/toast.js';
import { AuthShell } from './AuthShell.js';

/** docs/architecture/04 (screen: Login) — the split-panel treatment from
 * the design canvas (Login.dc.html), built with real tokens/components and
 * wired to the actual auth API rather than sample values. */
const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_not_configured: 'Single sign-on is not configured for this workspace.',
  sso_expired: 'That sign-in attempt expired. Please try again.',
  sso_invalid_state: 'That sign-in attempt could not be verified. Please try again.',
  sso_failed: 'Sign-in with Microsoft failed. Please try again or use your password.',
  sso_no_default_role:
    "Your workspace admin hasn't set a default role for new Microsoft sign-ins yet. Ask them to set one in Admin → Single sign-on, then try again.",
};

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
      <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
      <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
    </svg>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12" />
      {open ? <circle cx="12" cy="12" r="3" /> : <path d="M4 4 20 20" />}
    </svg>
  );
}

export function LoginPage() {
  const { login, loginError, verifyMfaChallenge, mfaVerifyError } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { error: ssoErrorCode } = useSearch({ from: '/login' });
  const ssoError = ssoErrorCode ? (SSO_ERROR_MESSAGES[ssoErrorCode] ?? 'Sign-in with Microsoft failed.') : undefined;
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { remember: false },
  });

  const onSubmit = async (input: LoginInput) => {
    const result = await login(input);
    if ('mfaRequired' in result) {
      setChallengeToken(result.challengeToken);
      return;
    }
    toast.success(`Welcome back, ${result.user.displayName}!`);
    await navigate({ to: '/' });
  };

  if (challengeToken) {
    return (
      <AuthShell>
        <MfaChallengeForm
          challengeToken={challengeToken}
          useRecoveryCode={useRecoveryCode}
          onToggleRecoveryCode={() => setUseRecoveryCode((v) => !v)}
          onVerify={verifyMfaChallenge}
          error={mfaVerifyError}
          onDone={(session) => {
            toast.success(`Welcome back, ${session.user.displayName}!`);
            void navigate({ to: '/' });
          }}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex w-full max-w-[400px] flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[22px] leading-7 font-[650] tracking-[-0.01em] text-ink">Sign in to Orion</h1>
          <p className="text-sm leading-5 text-text-muted">Welcome back. Use your work account or email.</p>
        </div>

        {ssoError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
            {ssoError}
          </div>
        )}

        <a
          href="/api/v1/auth/entra/start"
          className="flex h-11 items-center justify-center gap-2.5 rounded-input border border-border-strong bg-surface text-[15px] font-semibold text-ink shadow-card hover:bg-surface-subtle"
        >
          <MicrosoftLogo />
          Sign in with Microsoft
        </a>

        <div className="flex items-center gap-3 text-xs text-text-muted">
          <span className="h-px flex-1 bg-border" />
          or continue with email
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="flex flex-col gap-4">
          <Input
            label="Work email"
            type="email"
            uiSize="lg"
            autoComplete="username"
            error={errors.email?.message}
            {...register('email')}
          />
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              uiSize="lg"
              autoComplete="current-password"
              error={errors.password?.message}
              className="pr-11"
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute bottom-2 right-1.5 flex size-7 items-center justify-center rounded-[6px] text-text-muted hover:bg-surface-subtle hover:text-ink"
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-[13px] text-text-secondary">
              <input type="checkbox" className="size-4 accent-primary" {...register('remember')} />
              Keep me signed in on this device
            </label>
            <Link to="/forgot-password" className="text-[13px] font-semibold text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
        </div>

        {loginError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
            {loginError}
          </div>
        )}

        <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
          Sign in
        </Button>

        <div className="flex flex-col items-center gap-3 pt-1">
          <p className="text-[13px] text-text-secondary">Need access? Ask your workspace admin.</p>
          <p className="flex items-center gap-1.5 text-xs text-text-muted">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            Protected by rate limiting and audit logging.
          </p>
        </div>
      </form>
    </AuthShell>
  );
}

function MfaChallengeForm({
  challengeToken,
  useRecoveryCode,
  onToggleRecoveryCode,
  onVerify,
  onDone,
  error,
}: {
  challengeToken: string;
  useRecoveryCode: boolean;
  onToggleRecoveryCode: () => void;
  onVerify: (input: MfaVerifyInput & { challengeToken: string }) => Promise<SessionResponse>;
  onDone: (session: SessionResponse) => void;
  error?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MfaVerifyInput>({ resolver: zodResolver(mfaVerifySchema) });

  const onSubmit = async (input: MfaVerifyInput) => {
    const session = await onVerify({ ...input, challengeToken });
    onDone(session);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex w-full max-w-[400px] flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[22px] font-semibold tracking-tight text-ink">Two-factor verification</h1>
        <p className="text-sm text-text-muted">
          {useRecoveryCode ? 'Enter one of your recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </div>

      {useRecoveryCode ? (
        <Input label="Recovery code" autoComplete="one-time-code" error={errors.recoveryCode?.message} {...register('recoveryCode')} />
      ) : (
        <Input label="Authentication code" inputMode="numeric" autoComplete="one-time-code" error={errors.code?.message} {...register('code')} />
      )}

      {error && (
        <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
          {error}
        </div>
      )}

      <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
        Verify
      </Button>
      <button type="button" onClick={onToggleRecoveryCode} className="text-center text-[13px] font-medium text-text-muted hover:text-ink">
        {useRecoveryCode ? 'Use my authenticator app instead' : "I don't have my authenticator app"}
      </button>
    </form>
  );
}
